import { useEffect, useCallback, useRef } from 'react';
import socketService from '../../../../services/socketService';

/**
 * Hook for handling WebSocket events in the review system
 */
export const useReviewWebSocket = (creatorId, onUpdate) => {
  // Keep the latest onUpdate in a ref so the socket effect never needs it in deps
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  // Subscribe to review updates
  useEffect(() => {
    if (!creatorId) return;

    const handleReviewUpdate = (data) => {
      console.log('Review updated via WebSocket:', data);
      if (data.creatorId === creatorId && onUpdateRef.current) {
        onUpdateRef.current(data.review);
      }
    };

    const handleCreatorStatusChange = (data) => {
      console.log('Creator status changed via WebSocket:', data);
      if (data.creatorId === creatorId && onUpdateRef.current) {
        onUpdateRef.current({ creatorStatus: data.newStatus });
      }
    };

    // Subscribe to events
    socketService.on('review:updated', handleReviewUpdate);
    socketService.on('creator:status:changed', handleCreatorStatusChange);

    // Join creator room for targeted updates
    socketService.emit('join:creator', { creatorId });

    return () => {
      // Unsubscribe from events
      socketService.off('review:updated', handleReviewUpdate);
      socketService.off('creator:status:changed', handleCreatorStatusChange);

      // Leave creator room
      socketService.emit('leave:creator', { creatorId });
    };
  }, [creatorId]); // onUpdate intentionally excluded — use the ref instead

  // Emit review update
  const emitReviewUpdate = useCallback((reviewData) => {
    socketService.emit('review:update', reviewData);
  }, []);

  // Emit status change
  const emitStatusChange = useCallback((creatorId, newStatus, previousStatus) => {
    socketService.emit('creator:status:change', {
      creatorId,
      newStatus,
      previousStatus
    });
  }, []);

  return {
    emitReviewUpdate,
    emitStatusChange
  };
};